import { baseApi, resourceScopeTag, transportRequest } from '@krp/client-runtime'

import { AQSI_URLS } from '../../../shared/api/urls'
import { arrayOf, boolOf, idOf, objectOf, optionalText, textOf } from '../../../shared/api/wire'
import type { OwnWorker } from '../model/types'

/** Кассиры нашей системы, связанные с кассиром этого кабинета (RP-CU4): кого показать в «Начале работы». */
export function decodeOwnWorkers(raw: unknown): OwnWorker[] {
  const value = objectOf(raw, 'own cashiers')
  return arrayOf(value.items, 'items').map((entry, index) => {
    const field = `items[${index}]`
    const item = objectOf(entry, field)
    return {
      cashierId: idOf(item.cashierId, `${field}.cashierId`),
      displayName: textOf(item.displayName, `${field}.displayName`),
      position: optionalText(item.position, `${field}.position`),
      sourceCashierId: idOf(item.sourceCashierId, `${field}.sourceCashierId`),
      sourceCashierName: optionalText(item.sourceCashierName, `${field}.sourceCashierName`) ?? '',
      hasCode: boolOf(item.hasCode, `${field}.hasCode`),
    }
  })
}

export const ownCashiersRmkApi = baseApi.injectEndpoints({
  endpoints: (build) => ({
    aqsiOwnCashiers: build.query<OwnWorker[], { resourceId: string }>({
      query: ({ resourceId }) => ({ url: AQSI_URLS.ownCashierWorkers, params: { resourceId }, decode: decodeOwnWorkers }),
      providesTags: (_result, _error, { resourceId }) => [resourceScopeTag(resourceId)],
    }),
  }),
})
export const { aqsiOwnCashiers } = ownCashiersRmkApi.endpoints

/**
 * Вход кассира нашей системы (RP-CU4): выбранный кассир и его код → билет входа этой вкладки и рабочие места. Код — только в теле запроса,
 * без повтора; вкладку и устройство транспорт ставит заголовками сам.
 */
export const signInOwnCashier = (body: { code: string; cashierId: string }) =>
  transportRequest<{ ticket: string; workplaces: { resourceId: string; sourceCashierId: string }[] }>({
    url: AQSI_URLS.ownCashierSignIn, method: 'POST', body, noRetry: true,
    decode: (raw) => {
      const value = objectOf(raw, 'own cashier sign-in')
      return {
        ticket: idOf(value.ticket, 'ticket'),
        workplaces: arrayOf(value.workplaces, 'workplaces').map((entry, index) => {
          const item = objectOf(entry, `workplaces[${index}]`)
          return { resourceId: idOf(item.resourceId, 'resourceId'), sourceCashierId: idOf(item.sourceCashierId, 'sourceCashierId') }
        }),
      }
    },
  })

/** Пропуск РМК на кассира этого кабинета по билету входа: тот же, что даёт прежний вход по коду кассира кабинета. */
export const grantOwnCashier = (body: { ticket: string; resourceId: string; sourceCashierId: string }) =>
  transportRequest<{ grant: string; expiresAt: string }>({
    url: AQSI_URLS.ownCashierGrant, method: 'POST', body, noRetry: true,
    decode: (raw) => {
      const value = objectOf(raw, 'own cashier grant')
      const expiresAt = idOf(value.expiresAt, 'expiresAt')
      if (!Number.isFinite(Date.parse(expiresAt))) throw new Error('Некорректный срок подтверждения кассира')
      return { grant: idOf(value.grant, 'grant'), expiresAt }
    },
  })
